import {
  DataStatus,
  FormField,
  Form,
  FormSubmitHandler,
  ButtonGroup,
  FormButton,
  Separator,
  DialogFooter,
} from 'platform/components';
import {Grid, Heading, Show, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {
  useGetServiceWorkItemIntegrationClaraInformationQuery,
  usePatchServiceWorkItemIntegrationClaraInformationMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError, PredefinedTextArea} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

interface EditItemAdditionalInformationProps extends TestIdProps {
  orderId: string;
  serviceCaseId: string;
  serviceJobId: string;
  serviceItemId: string;
}

export function EditItemAdditionalInformation(props: EditItemAdditionalInformationProps) {
  const {
    data: serviceWorkItemClaraInformation,
    isError: isServiceWorkItemClaraInformationError,
    isLoading: isServiceWorkItemClaraInformationLoading,
  } = useGetServiceWorkItemIntegrationClaraInformationQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.orderId,
    serviceJobId: props.serviceJobId,
    serviceItemId: props.serviceItemId,
  });
  const [patchServiceWorkItemIntegrationClaraInformation] =
    usePatchServiceWorkItemIntegrationClaraInformationMutation();

  const handleSubmit: FormSubmitHandler<FormType> = async (data) =>
    await patchServiceWorkItemIntegrationClaraInformation({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
      serviceJobId: props.serviceJobId,
      serviceItemId: props.serviceItemId,
      body: {...data, costCategory: data.otherCost ? data.costCategory : null},
    })
      .unwrap()
      .catch(handleApiError);

  return (
    <DataStatus
      isLoading={isServiceWorkItemClaraInformationLoading}
      isError={isServiceWorkItemClaraInformationError}
    >
      <VStack spacing={1}>
        <Heading size={4}>{i18n.t('entity.workItem.labels.claraIntegration')}</Heading>
        <Text size="xSmall" color="secondary">
          {i18n.t('entity.workItem.labels.claraIntegrationDescription')}
        </Text>
      </VStack>
      <Separator />
      <Form<FormType>
        onSubmit={handleSubmit}
        experimentalZodSchema={formSchema}
        defaultValues={{
          otherCost: serviceWorkItemClaraInformation?.otherCost,
          costCategory: serviceWorkItemClaraInformation?.costCategory,
          reasonForAdditionalLabour: serviceWorkItemClaraInformation?.reasonForAdditionalLabour,
        }}
      >
        {(control, formApi) => (
          <VStack spacing={4}>
            <PredefinedTextArea
              formApi={formApi}
              name="reasonForAdditionalLabour"
              resource="workItem"
              context="reasonForAdditionalLabour"
              label={i18n.t('entity.workItem.labels.reasonForAdditionalLabour')}
              isDisabled={formApi.watch('otherCost')}
              data-testid={suffixTestId('reasonForAdditionalLabour', props)}
            />
            <FormField
              control={control}
              name="otherCost"
              type="switch"
              onChange={(value) => {
                if (value) {
                  formApi.setValue('reasonForAdditionalLabour', '');
                }
              }}
              label={i18n.t('entity.workItem.labels.otherCost')}
            />
            <Show when={formApi.watch('otherCost')}>
              <Grid columns={2}>
                <FormField
                  control={control}
                  name="costCategory"
                  type="choice"
                  options={[
                    {
                      label: i18n.t('entity.workItem.labels.miscellaneous'),
                      value: 'MISCELLANEOUS',
                    },
                    {
                      label: i18n.t('entity.workItem.labels.mileageAllowance'),
                      value: 'MILEAGE_ALLOWANCE',
                    },
                    {label: i18n.t('entity.workItem.labels.towing'), value: 'TOWING'},
                    {
                      label: i18n.t('entity.workItem.labels.chargingCosts'),
                      value: 'CHARGING_COSTS',
                    },
                    {
                      label: i18n.t('entity.workItem.labels.foreignLabor'),
                      value: 'FOREIGN_LABOR',
                    },
                  ]}
                  label={i18n.t('entity.workItem.labels.costCategory')}
                  menuInPortal
                  isRequired
                  isNotClearable
                />
              </Grid>
            </Show>
            <DialogFooter>
              <ButtonGroup align="right">
                <FormButton
                  control={control}
                  type="submit"
                  title={i18n.t('general.actions.saveChanges')}
                  data-testid={suffixTestId('save', props)}
                />
              </ButtonGroup>
            </DialogFooter>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}

const formSchema = z
  .object({
    otherCost: z.boolean(),
    costCategory: z
      .enum(['MISCELLANEOUS', 'MILEAGE_ALLOWANCE', 'TOWING', 'CHARGING_COSTS', 'FOREIGN_LABOR'])
      .nullable(),
    reasonForAdditionalLabour: z.string().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.otherCost && !data.costCategory) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('general.errors.mixed.required'),
        path: ['costCategory'],
      });
    }
  });

type FormType = z.infer<typeof formSchema>;
